import React from "react";
import { useNavigate } from "react-router-dom";
import "./PiMenuButton.css";
import classNames from "classnames";
import { OnboardingTargetId } from "../onboarding/onboardingTargets";

interface PiMenuButtonProps {
  text: string;
  id?: string;
  /**
   * Onboarding anchor for the rendered button. Deliberately separate from
   * `id`: see the registry doc comment for why tour anchors are never reused
   * from ids or test ids.
   */
  onboardingId?: OnboardingTargetId;
  icon?: React.ReactNode;
  link?: string;
  doHighlight?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
  externalLink?: boolean;
  variant?: "default" | "modern";
  tone?: "default" | "danger";
}

export default function PiMenuButton(props: PiMenuButtonProps) {
  const navigate = useNavigate();

  const onClick = () => {
    if (props.disabled) return; // ignore if button is disabled

    if (props.link) {
      if (props.externalLink) {
        window.open(props.link, "_blank");
      } else {
        navigate(props.link);
      }
    }
    if (props.onClick) {
      props.onClick();
    }
  };

  const label = (
    <div className="pimenu-button-label">
      <div className="pimenu-button-label-content">{props.label}</div>
    </div>
  );

  return (
    <button
      id={props.id}
      data-onboarding-id={props.onboardingId}
      className={classNames({
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors cursor-pointer outline-none text-left font-poppins":
          props.variant === "modern",
        "hover:bg-creator-primary-background": props.variant === "modern" && !props.disabled,
        "opacity-55 pointer-events-none": props.variant === "modern" && props.disabled,
        "flex overflow-x-hidden overflow-y-hidden align-middle text-left h-[50px]  font-[14px] cursor-pointer w-full rounded-md select-none":
          props.variant !== "modern",
        "bg-creator-primary-washed": props.variant !== "modern" && props.doHighlight,
        "hover:bg-interaction-text-hoverBackground": props.variant !== "modern",
        "bg-creator-primary-background  pointer-events-none text-creator-primary-disabled":
          props.variant !== "modern" && props.disabled,
      })}
      onClick={() => onClick()}
    >
      {props.icon && (
        <div
          className={classNames({
            "flex items-center justify-center text-[20px] flex-shrink-0":
              props.variant === "modern",
            "text-creator-primary": props.variant === "modern" && props.tone !== "danger",
            "text-creator-error": props.variant === "modern" && props.tone === "danger",
            "px-2 text-[20px] my-auto flex w-[42px]": props.variant !== "modern",
            "text-creator-primary ": props.variant !== "modern" && props.doHighlight,
          })}
        >
          {props.icon}
        </div>
      )}
      <div
        className={classNames({
          "overflow-hidden whitespace-nowrap text-ellipsis text-[14px] font-medium":
            props.variant === "modern",
          "text-creator-text-sub": props.variant === "modern" && props.tone !== "danger",
          "text-creator-error": props.variant === "modern" && props.tone === "danger",
          "my-auto overflow-hidden whitespace-nowrap text-ellipsis text-[14px] ml-1":
            props.variant !== "modern",
          "text-creator-primary": props.variant !== "modern" && props.doHighlight,
        })}
      >
        {props.text}
      </div>
      {props.label && label}
    </button>
  );
}
